<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Students Management
            </h1>
            <div class="flex flex-wrap items-center gap-3">
                <button @click="openImportModal" class="btn btn-success">
                    Import Student
                </button>
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Student
                </button>
            </div>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by name, NIS, or email..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Class Filter -->
                    <SearchableSelect
                        v-model="filters.current_class_id"
                        :options="classOptions"
                        placeholder="All Classes"
                        option-label="name"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchStudents"
                    />

                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="studentStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="5" />
        </div>

        <!-- Error State -->
        <div
            v-if="studentStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ studentStore.error }}
                    </p>
                    <button
                        @click="studentStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!studentStore.loading && !studentStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Student
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Student ID
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                NIS
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Class
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="student in studentStore.students"
                            :key="student.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div class="flex items-center">
                                    <div class="m-0">
                                        <div
                                            class="text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            {{
                                                student.user?.name || "Unknown"
                                            }}
                                        </div>
                                        <div
                                            class="text-sm text-gray-500 dark:text-gray-400"
                                        >
                                            {{
                                                student.user?.email ||
                                                "No email"
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ student.student_id }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ student.nisn }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ student.current_class?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="editStudent(student)"
                                    title="Edit Student"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(student)"
                                    title="Delete Student"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="studentStore.students.length === 0"
                :icon="GraduationCap"
                title="No students found"
                description="Get started by adding a new student."
            >
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Student
                </button>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="studentStore.meta"
                :per-page="studentStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- Student Modal -->
        <StudentModal
            v-if="showModal"
            :show-modal="showModal"
            :student="selectedStudent"
            :is-editing="isEditing"
            @close="closeModal"
            @save="handleSave"
            @saved="handleSaved"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            v-if="showDeleteModal"
            :show="showDeleteModal"
            :title="deleteModalTitle"
            :message="deleteModalMessage"
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />

        <!-- Import Student Modal -->
        <StudentImportModal
            v-if="showImportModal"
            @close="closeImportModal"
            @success="handleImportSuccess"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useStudentStore } from "@/stores/studentStore";
import { useClassSchoollStore } from "@/stores/classSchoollStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import StudentModal from "@/components/StudentModal.vue";
import StudentImportModal from "@/components/StudentImportModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import NotificationService from "@/utils/notifications";
import type { Student } from "@/api/student";
import { Plus, RotateCcw, Pencil, Trash2, GraduationCap } from "@lucide/vue";

const studentStore = useStudentStore();
const classStore = useClassSchoollStore();
// State
const searchQuery = ref("");
const filters = ref({
    current_class_id: "",
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const showImportModal = ref(false);
const selectedStudent = ref<Student | null>(null);
const isEditing = ref(false);
const studentToDelete = ref<Student | null>(null);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this student? This action cannot be undone.",
);

// Options for Class filter
const classOptions = computed(() => {
    const options = [{ id: "", name: "All Classes" }];
    classStore.classes.forEach((classItem: any) => {
        options.push({ id: classItem.id, name: classItem.name });
    });
    return options;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.current_class_id !== "" ||
        studentStore.page !== 1 ||
        studentStore.perPage !== 10
    );
});

// Methods
const handlePerPageChange = (value: number) => {
    studentStore.perPage = value;
    studentStore.page = 1;
    fetchStudents();
};

const fetchStudents = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await studentStore.fetchStudents(searchFilters);
};

const debouncedSearch = useDebounce(fetchStudents, 300);

const openModal = () => {
    selectedStudent.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const editStudent = (student: Student) => {
    selectedStudent.value = student;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedStudent.value = null;
    isEditing.value = false;
};

const confirmDelete = (student: Student) => {
    studentToDelete.value = student;
    deleteModalTitle.value = "Delete Student";
    deleteModalMessage.value = `Are you sure you want to delete ${student.user?.name || "this student"}? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    studentToDelete.value = null;
};

const handleSave = async () => {
    // This function is now handled within the modal
    // Validation errors are handled in modal and don't reach here
    // Only successful operations will trigger to 'saved' event
};

const handleSaved = () => {
    // Refresh the students list to show new/updated student
    fetchStudents();
    showNotification(
        isEditing.value
            ? "Student updated successfully"
            : "Student created successfully",
        "success",
    );
    closeModal();
};

const handleDelete = async () => {
    try {
        if (studentToDelete.value) {
            await studentStore.deleteStudent(studentToDelete.value.id);
            showNotification("Student deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete student", "error");
    }
};

const openImportModal = () => {
    showImportModal.value = true;
};

const closeImportModal = () => {
    showImportModal.value = false;
};

const handleImportSuccess = () => {
    showNotification("Students imported successfully", "success");
    fetchStudents();
};

const goToPage = (page: number) => {
    studentStore.setPage(page);
    fetchStudents();
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value.current_class_id = "";
    studentStore.resetFilters();
    fetchStudents();
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    classStore.fetchClasses();
    fetchStudents();
});
</script>
